import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import CircularProgress from '@mui/material/CircularProgress';

import { signup } from '../../api';
import { AuthForm, AuthFormButton, AuthFormInput, AuthFormLabel } from '../../features/auth';
import logo from '../../images/logo-login.png';
import { translateAuthError } from '../../lib/authError';
import LanguageControl from '../../shared/components/LanguageControl';

function Signup(props: any) {
    const { setCompanyName, setLogin } = props;
    const { t, i18n } = useTranslation(undefined, { keyPrefix: 'Login' });
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [stage, setStage] = useState('entry');
    const [emailDisplay, setEmailDisplay] = useState('');
    // Both password fields are controlled so the confirmation can be checked as the user
    // types, rather than only on submit.
    const [password, setPassword] = useState('');
    const [passwordConfirm, setPasswordConfirm] = useState('');

    const passwordsMatch = password === passwordConfirm;
    // Only complain once there is something to compare against — an empty confirm field
    // still blocks submission, it just isn't flagged as an error before it's been filled.
    const showMismatch = passwordConfirm.length > 0 && !passwordsMatch;

    const onSubmit = (event: any) => {
        event.preventDefault();
        setLoading(true);
        const email = event.target.email.value;
        const displayName = email;
        const agree = event.target.agree.checked;

        // Backstop for the disabled submit button (e.g. implicit submission).
        if (!passwordsMatch) {
            setLoading(false);
            return;
        }

        const emailRegex = /^[A-Za-z0-9_!#$%&'*+/=?`{|}~^.-]+@[A-Za-z0-9.-]+$/;
        const passwordRegex = /^(?=.*[A-Za-z])(?=.*\d)[\w\W]{8,}$/;

        if (!emailRegex.test(email)) {
            alert(t('invalidEmail'));
            setLoading(false);
            return;
        }

        if (!passwordRegex.test(password)) {
            alert(t('passwordCheck'));
            setLoading(false);
            return;
        }

        if (!agree) {
            alert(t('mustAgreeTerms'));
            setLoading(false);
            return;
        }

        signup(email, password, displayName, '', '', i18n.language)
            .then((data: any) => {
                if (data && data.success) {
                    localStorage.setItem('token', data.token);
                    setCompanyName(data.companyName);
                    setLogin(true);
                    setEmailDisplay(email);
                    setLoading(false);
                    setStage('success');
                } else {
                    // Legacy path: backend returns 200 with { error } in body.
                    setLoading(false);
                    alert(translateAuthError(t, data, 'authError.signupFailed'));
                }
            })
            .catch((error) => {
                // Post-status-code-fix path: 400/409/500 rejects here with
                // the reason on error.response.data.error. Network errors
                // (no response) fall through to 'authError.connectionFailed'.
                setLoading(false);
                alert(translateAuthError(t, error, 'authError.connectionFailed'));
            });
    };

    const redirectToLogin = () => {
        navigate('/login');
    };

    if (loading) {
        return <CircularProgress />;
    }

    return (
        <div className='container-auth'>
            <AuthForm
                className={`${stage === 'entry' ? 'flex' : 'hidden'} gap-y-5`}
                logoSrc={logo}
                onLogoClick={redirectToLogin}
                topRight={<LanguageControl className='hidden md:block' />}
            >
                <p className='mt-16 text-xl font-semibold text-[#757575] md:mt-24 md:text-gray-700 md:font-bold'>
                    {t('signup')}
                </p>
                <form onSubmit={onSubmit} className='mt-2 flex w-[360px] flex-col items-center md:w-full'>
                    <AuthFormLabel htmlFor='email'>
                        {t('email')}
                        <span className='text-red-500'>*</span>
                    </AuthFormLabel>
                    <AuthFormInput id='email' name='email' type='text' autoComplete='email' />
                    <AuthFormLabel htmlFor='password'>
                        {t('password')}
                        <span className='text-red-500'>*</span>
                    </AuthFormLabel>
                    <AuthFormInput
                        id='password'
                        name='password'
                        type='password'
                        className='mb-2'
                        autoComplete='new-password'
                        value={password}
                        onChange={(event: any) => setPassword(event.target.value)}
                    />
                    {/* Confirm sits directly below password so the two fields read as one
                        group; the hint (which describes password requirements) follows both. */}
                    <AuthFormLabel htmlFor='passwordConfirm'>
                        {t('confirmPassword')}
                        <span className='text-red-500'>*</span>
                    </AuthFormLabel>
                    <AuthFormInput
                        id='passwordConfirm'
                        name='passwordConfirm'
                        type='password'
                        className='mb-2'
                        autoComplete='new-password'
                        value={passwordConfirm}
                        onChange={(event: any) => setPasswordConfirm(event.target.value)}
                        aria-invalid={showMismatch}
                        aria-describedby={showMismatch ? 'passwordConfirm-error' : undefined}
                    />
                    <p className='w-full text-xs text-gray-400 md:w-[20rem]'>{t('passwordCheck')}</p>
                    {showMismatch && (
                        <p id='passwordConfirm-error' role='alert' className='w-full text-xs text-red-500 md:w-[20rem]'>
                            {t('passwordMismatch')}
                        </p>
                    )}
                    <div className='mt-4 flex w-full items-center justify-center rounded-full px-[10.5px] pb-[9.5px] pt-[10.5px] text-[12px] md:mt-0 md:w-[21rem] md:justify-start md:p-0'>
                        <input type='checkbox' id='agree' className='mr-2 accent-[#1E2B7C]' />
                        <label className='text-sm'>
                            {t('agree')}{' '}
                            <a
                                href='https://www.gocochain.com/privacy-policy-and-terms'
                                className='text-blue-600 hover:underline'
                            >
                                {t('termOfUse')}
                            </a>{' '}
                            {t('and')}{' '}
                            <a
                                href='https://www.gocochain.com/privacy-policy-and-terms'
                                className='text-blue-600 hover:underline'
                            >
                                {t('privacyPolicy')}
                            </a>
                        </label>
                    </div>
                    <AuthFormButton className='mt-6 p-[8px] md:mt-2' disabled={!passwordsMatch}>
                        {t('createAccount')}
                    </AuthFormButton>
                </form>
                <div className='mt-2'>
                    <p className='text-sm'>
                        {t('alreadyHave')}{' '}
                        <Link to='/login' className='text-blue-600 hover:underline'>
                            {t('login')}
                        </Link>
                    </p>
                </div>
                <div className='block md:hidden'>
                    <LanguageControl />
                </div>
            </AuthForm>

            <AuthForm
                className={`${stage === 'success' ? 'flex' : 'hidden'}`}
                logoSrc={logo}
                onLogoClick={redirectToLogin}
                topRight={<LanguageControl className='hidden md:block' />}
            >
                <p className='mb-8 mt-16 text-[16px] font-bold text-[#757575] md:text-xl md:text-gray-700'>
                    {t('checkInbox')}
                </p>
                <p className='mb-2 text-[10px] text-[#757575] md:text-base md:text-gray-700'>{t('checkNote1')}</p>
                <p className='mb-2 text-black md:text-blue-600 md:text-base'>{emailDisplay}</p>
                <p className='mb-12 text-[10px] text-[#757575] md:text-base md:text-gray-700'>{t('checkNote2')}</p>
                <div className='w-full text-center md:absolute md:bottom-2 md:left-1/2 md:-translate-x-1/2 md:mt-0 mt-[100px]'>
                    <p className='text-sm'>
                        {t('question')}{' '}
                        <a href='mailto:support@gocochain.com' className='text-blue-600 hover:underline'>
                            support@gocochain.com
                        </a>
                    </p>
                </div>
                <div className='block md:hidden'>
                    <LanguageControl />
                </div>
            </AuthForm>
        </div>
    );
}

export default Signup;
